import * as React from 'react';
import SectionControl, { ISectionInfo, ISectionHeaderSettings } from '../SectionControl';
import { Section, getLabel } from '../../../../entities/Metadata';
import { Dictionary, EMPTY_GUID, EntityType, IWarning, UserPreferencesSettingsUpdate, entityLogoConfig, IWithName } from "../../../../entities/common";
import { css, getId, Icon, IconButton } from 'office-ui-fabric-react';
import { waitForFinalEvent } from "../../../utils/common";
import { RouteComponentProps } from "react-router-dom";
import { ISectionReports } from '../../../reporting/SectionReports';
import { LayoutsState } from '../../../../store/layouts';
import { AccordionSectionRef } from '../../AccordionSection';
import { IControlConfiguration } from '../../interfaces/ISectionUIControlProps';
import { nameof } from '../../../../store/services/metadataService';
import { actionCreators as UserInterfaceActionCreators, UserInterfaceState } from '../../../../store/UserInterfaceStore';
import { ILogoProps } from "../../Logo";
import { OnSaveSettings } from '../withSectionsSettings';

export interface IContainerSectionInfo extends ISectionInfo {
    isInView: boolean;
}

interface State {
    sections: IContainerSectionInfo[];
    lastUpdatedSectionIndex?: number;
    isExpanded: boolean;
}

type SectionsSettings = Dictionary<Dictionary<SectionSettings>> & {
    layoutId?: string;
}

type SectionContainerData = {
    id: string;
    imageId?: string;
    attributes?: Dictionary<any>;
    sections?: Section[];
    hiddenActionsContainerIds?: string[];
    isEditable?: boolean;
    canConfigure?: boolean;
    canCollaborate?: boolean;
};

export interface BaseSectionsContainerProps {
    entity: SectionContainerData;
    sectionHeaderSettings?: ISectionHeaderSettings;
    hideSideBar?: boolean;
    getSections?: (entity: any) => Section[];
    warnings?: IWarning[];
    onHeaderMiddleRender?: (section: IContainerSectionInfo) => JSX.Element | JSX.Element[] | null;
    onSaveSettings?: OnSaveSettings;
    layouts?: LayoutsState;
    isUpdatingSections?: boolean;
    reporting?: ISectionReports;
    sectionsSettings?: SectionsSettings;
    entityType?: EntityType;
    controlsConfig?: Dictionary<IControlConfiguration>;
    userInterface: UserInterfaceState;
    userInterfaceActions: typeof UserInterfaceActionCreators;
    withViewsInHeader?: boolean;
    isExpanded?: boolean;
    onExpand?: (expanded: boolean) => void;
    className?: string;
}

type SectionSettings = { isOpen: boolean } & Dictionary<unknown>;

type Props = BaseSectionsContainerProps & RouteComponentProps<{}>;

export class BaseSectionsContainer extends React.Component<Props, State> {
    private sections: Dictionary<AccordionSectionRef> = {};
    private sideBar: any[] = [];
    private scrollContainer: any;
    private highlightSideBarDebounce = waitForFinalEvent(this.highlightSideBar.bind(this), 50, getId());

    private sectionNameToEntityType: Dictionary<string> = {
        "Key dates": EntityType.KeyDate,
        "Iterations": EntityType.Iteration,
        "Risks": EntityType.Risk,
        "Issues": EntityType.Issue,
        "Lessons learned": EntityType.LessonLearned,
        "Action items": EntityType.ActionItem,
        "Steering committee": EntityType.SteeringCommittee,
        "Key Decisions": EntityType.KeyDecision,
        "Change Requests": EntityType.ChangeRequest,
        "Purchase Orders": EntityType.PurchaseOrder,
        "Invoices": EntityType.Invoice,
        "Deliverables": EntityType.Deliverable,
        "Stages": EntityType.ProcessStage,
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            ...this._buildState(props),
            isExpanded: props.isExpanded ?? true,
        };
    }

    private _buildState(props: BaseSectionsContainerProps) {
        const query = new URLSearchParams(this.props.location.search);
        const isInQuery = (name: string) => !!this.sectionNameToEntityType[name] && !!query.get(this.sectionNameToEntityType[name]);
        const headerSettings = this._buildSectionHeaderSettings();
        return {
            sections: (props.layouts?.activeEntity ? props.layouts?.activeEntity.sections : props.getSections?.(props.entity) ?? props.entity.sections!)
                .filter(_ => _.isSelected)
                .map(_ => ({
                    ..._,
                    isInView: false,
                    isConfigureMode: false,
                    isOpen: isInQuery(_.name) || this._isOpen(_, getSectionSettings(_.id, props.sectionsSettings)),
                    headerSettings: headerSettings
                })),
        };
    }

    private _buildSectionHeaderSettings(): ISectionHeaderSettings {
        if (this.props.sectionHeaderSettings) {
            return this.props.sectionHeaderSettings;
        }
        const { entity } = this.props;
        const entityName = entity?.attributes?.[nameof<IWithName>('Name')];
        const entityLogoCfg = this.props.entityType ? entityLogoConfig[this.props.entityType] : undefined;
        const logoProps: ILogoProps | undefined = entityLogoCfg
            ? {
                title: entityLogoCfg?.label ?? entityName,
                imageId: entity?.imageId,
                iconName: entityLogoCfg?.cssClass ? undefined : entityLogoCfg?.iconName,
                className: entityLogoCfg?.cssClass
            }
            : undefined;
        return {
            label: entityName,
            logo: logoProps
        }
    }

    componentDidUpdate(prevProps: BaseSectionsContainerProps, prevState: State) {
        this.highlightSideBar();

        const isLayoutApplied = prevProps.layouts?.isApplyingLayout && !this.props.layouts?.isApplyingLayout;
        const isSectionsUpdated = prevProps.isUpdatingSections && !this.props.isUpdatingSections;
        if (!isLayoutApplied && !isSectionsUpdated) {
            const index = this.state.lastUpdatedSectionIndex;
            if (!index) {
                return;
            }

            const prevSection = prevState.sections[index];
            const section = this.state.sections[index];
            if (!prevSection || !section) {
                return;
            }

            if (!prevSection.isOpen && section.isOpen) {
                const node = this.sections[section.id]?.getRef();
                if (!node) {
                    return;
                }

                if (typeof (node as any).scrollIntoViewIfNeeded === "function") {
                    (node as any).scrollIntoViewIfNeeded();
                    return;
                }
                node.scrollIntoView();
            }
        }
    }

    componentDidMount() {
        this.highlightSideBar();
    }

    componentWillReceiveProps(nextProps: BaseSectionsContainerProps) {
        const newState: State = {
            ... this._buildState(nextProps),
            isExpanded: nextProps.isExpanded === this.props.isExpanded
                ? this.state.isExpanded
                : nextProps.isExpanded ?? true
        };
        const isLayoutApplied = this.props.layouts?.isApplyingLayout && !nextProps.layouts?.isApplyingLayout;
        const isLayoutChanged = this.props.layouts?.activeEntityId !== nextProps.layouts?.activeEntityId;
        const isSectionsUpdated = this.props.isUpdatingSections && !nextProps.isUpdatingSections;
        const isSettingsLoaded = !this.props.onSaveSettings && !!nextProps.onSaveSettings;
        if (!isLayoutApplied && !isSectionsUpdated && !isLayoutChanged && !isSettingsLoaded) {
            newState.sections.forEach(_ => {
                const oldSection = this.state.sections.find(__ => __.id === _.id);
                if (oldSection) {
                    _.isInView = oldSection.isInView;
                    _.isConfigureMode = oldSection.isConfigureMode;
                    _.isOpen = oldSection.isOpen;
                }
            });
        }

        this.sections = {};
        this.sideBar = [];
        this.setState(newState);
    }

    private _isOpen = (section: Section, sectionSettings?: SectionSettings) => {
        return sectionSettings?.isOpen === undefined ? section.isOpen : sectionSettings?.isOpen;
    }

    highlightSideBar() {
        if (this.props.hideSideBar) {
            return;
        }

        Object.values(this.sections).forEach((value, index) => {
            if (this.isVisible(value?.getRef())) {
                this.sideBar[index].classList.add('in-view');
            } else {
                this.sideBar[index].classList.remove('in-view');
            }
        });
    }

    isVisible(element?: HTMLDivElement | null) {
        if (this.scrollContainer && element) {
            const offset = 10;

            const containerRect = this.scrollContainer.getBoundingClientRect();
            const componentRect = element.getBoundingClientRect();

            const containerTop = containerRect.top + offset;
            const containerBottom = containerRect.bottom - offset;

            return componentRect.top < containerBottom && componentRect.bottom > containerTop;
        }

        return false;
    }

    render() {
        const { hideSideBar, withViewsInHeader, entity, warnings, reporting, layouts, sectionsSettings,
            controlsConfig, onHeaderMiddleRender, entityType, onSaveSettings } = this.props;
        const { sections = [], isExpanded } = this.state;

        return <div key={getLayoutId(sectionsSettings)} 
            className={css("view", this.props.className, withViewsInHeader && "with-views-in-header", (this.props.hideSideBar || !isExpanded) && "with-sidebar")}>
            {!hideSideBar && <div className={css("sidebar", !isExpanded && "collapsable")}>
                <IconButton
                    className='chevron'
                    iconProps={{ iconName: isExpanded ? 'ChevronLeft' : 'ChevronRight' }}
                    onClick={() => {
                        this.setState({ isExpanded: !isExpanded }, () => window.dispatchEvent(new Event("resize")));
                        this.props.onExpand?.(!isExpanded);
                    }} />
                <div className="items">
                    {sections.map((section, index) =>
                        <div key={section.id} aria-hidden
                            ref={_ => this.sideBar[index] = _}
                            className={css("menu align-center", section.isInView && 'in-view')}
                            onClick={_ => this._expandAccordionAndScroll(section.id)}
                            title={getLabel(section)}>
                            {section.settings.iconName && <Icon iconName={section.settings.iconName} />}
                            <span className='menu-text'>{section.navigationLabel ?? getLabel(section)}</span>
                        </div>)}
                </div>
            </div>}
            <div className={`content scrollable`} ref={_ => this.scrollContainer = _} onScroll={this.highlightSideBarDebounce}>
                <div className="sections-container">
                    {
                        sections.map((section, index) => <SectionControl
                            key={section.id}
                            ref={el => el && (this.sections[section.id] = el)}
                            section={section}
                            hideActions={entity.hiddenActionsContainerIds?.includes(section.id)}
                            index={index}
                            entity={entity}
                            entityType={entityType}
                            warnings={warnings}
                            onHeaderMiddleRender={onHeaderMiddleRender ? () => (onHeaderMiddleRender(section)) : undefined}
                            reporting={reporting}
                            isViewSelected={!!layouts?.activeEntity}
                            onChange={this._onChange}
                            onSaveSettings={onSaveSettings ? this._onSaveSettings : undefined}
                            sectionSettings={getSectionSettings(section.id, sectionsSettings)}
                            controlsConfig={controlsConfig}
                            onToggleFullScreen={_ => this.props.userInterfaceActions.toggleFullScreen(_, section.id)}
                            isFullScreen={this.props.userInterface.isFullScreen && section.id === this.props.userInterface.sectionId}
                            scrollToSection={_ => this._expandAccordionAndScroll(_)}
                        />)
                    }
                </div>
            </div>
        </div>;
    }

    private _onSaveSettings = (sectionId: string, update: UserPreferencesSettingsUpdate, sendToServer?: boolean) => {
        const { onSaveSettings, sectionsSettings } = this.props;

        const updateWithLayoutAndSectionIds: UserPreferencesSettingsUpdate = {
            ...update,
            parentSettingsPathKeys: [
                getLayoutId(sectionsSettings),
                sectionId,
                ...update.parentSettingsPathKeys
            ]
        };
        onSaveSettings!(updateWithLayoutAndSectionIds, sendToServer);
    }

    private _expandAccordionAndScroll = (sectionId: string) => {

        const node = this.sections[sectionId]?.getRef();
        const index = this.state.sections.findIndex(_ => _.id === sectionId);

        if (!node || index < 0) {
            return;
        }

        if (!this.state.sections[index].isOpen) {
            this.sections[sectionId]?.expand();

            //nail: checks if section has dynamic content which is loaded with delay.
            //If the size of the section is stretched by the content, re-scrollIntoView() is needed
            //whole duration of check cannot be too long to avoid UI artifacts
            let count = 10;
            let height = node.clientHeight;
            const interval = setInterval(() => {
                if (height !== node.clientHeight) {
                    height = node.clientHeight;
                    node.scrollIntoView();
                }
                if (!--count) {
                    clearInterval(interval);
                }
            }, 50);
        }
        node.scrollIntoView();
    }

    private _onChange = (index: number, changes: Partial<ISectionInfo>) => {
        const section = this.state.sections[index];

        const settings = getSectionSettings(section.id, this.props.sectionsSettings);
        if (changes.isOpen !== undefined && settings?.isOpen !== changes.isOpen) {
            this.props.onSaveSettings?.({
                parentSettingsPathKeys: [getLayoutId(this.props.sectionsSettings), section.id],
                valueBySettingNameMap: { [nameof<SectionSettings>("isOpen")]: changes.isOpen }
            })
        }

        const sections: IContainerSectionInfo[] = this.state.sections.map((section, i) => i === index ? Object.assign({}, section, changes) : section);
        this.setState({ sections, lastUpdatedSectionIndex: index }, () => this.highlightSideBar());
    }
}

const getSectionSettings = (sectionId: string, sectionsSettings?: SectionsSettings): SectionSettings | undefined => sectionsSettings?.[getLayoutId(sectionsSettings)]?.[sectionId];

const getLayoutId = (sectionsSettings?: SectionsSettings) => sectionsSettings?.layoutId
    ? sectionsSettings.layoutId
    : EMPTY_GUID